import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';
import {
  Route,
  Redirect,
  withRouter,
} from 'react-router-dom';
import classnames from 'classnames';

import { Render } from '@puckeditor/core';
import { injectIntl } from 'react-intl';
import { validate as uuidValidate } from 'uuid';
import { APIgetPage } from '../api/Pages';
import { fetchPageMetadata } from '../actions/Metadata';
import { isLoggedIn } from '../api/Auth';

import File from './File';
import PageUnlockAccess from './PageUnlockAccess';
import PageHeader from '../components/Page/Header';
import PageFooter from '../components/Page/Footer';
import PageCollectionView from './PageCollectionView';
import ExternalSearchResults from './SearchResults/ExternalSearchResults';
import Loading from '../components/Loading';
import LoadingMain from '../components/LoadingMain';
import NothingFound from '../components/NothingFound';
import FlashMessages from '../components/FlashMessages';

import { hideIntercomWidget } from '../helpers';
import { cleanLoginParams } from '../helpers/App';
import { PageIdContext } from '../helpers/PageIdContext';
import { getPuckConfig } from '../helpers/PuckConfig';
import { getSetting } from '../helpers/Settings';
import {
  getSectionFrontendComponent,
  getPageUrl,
  sectionComponents,
  shouldDisableTransparentHeaderPadding,
  setPageFileModalScrollLock,
} from '../helpers/PageSections';
import {
  buildPageFontPresentation,
  resolvePageFontValues,
} from '../helpers/PageFonts';
import Actionbar from '../components/Actionbar';

class Page extends Component {
  constructor(props) {
    super(props);

    this.state = {
      page: null,
      redirect: false,
      notFound: false,
      error: null,
      accessLocked: false,
      errorScreen: null,
      passwordError: false,
    };

    this.pageContextValue = null;
    this.previousLocation = props.location;
    this.puckRenderCache = {
      page: null,
      disableTransparentHeaderPadding: null,
      pageAccentMode: null,
      pageAccentCustom: null,
      pagePrimaryFontValue: null,
      pageSecondaryFontValue: null,
      data: null,
    };
    this.submitAccess = this.submitAccess.bind(this);
  }

  componentDidMount() {
    const { location } = this.props;
    this.fetchPage();
    setPageFileModalScrollLock(this.isOverlayModalRoute(location));
    hideIntercomWidget();
  }

  componentDidUpdate(prevProps) {
    const { match, location } = this.props;
    const { pageId } = match.params;
    const { pageId: prevPageId } = prevProps.match.params;

    if (!this.isFileModalRoute(location)) {
      this.previousLocation = location;
    }

    const isModal = this.isOverlayModalRoute(location);
    const wasModal = this.isOverlayModalRoute(prevProps.location);

    if (isModal !== wasModal) {
      setPageFileModalScrollLock(isModal);
    }

    if (pageId !== prevPageId) {
      this.setState(
        {
          page: null,
          notFound: false,
          redirect: false,
          error: null,
        },
        () => {
          this.fetchPage();
        },
      );
    }
  }

  // eslint-disable-next-line class-methods-use-this
  componentWillUnmount() {
    this.pageMetadataController?.abort();
    setPageFileModalScrollLock(false);
  }

  // eslint-disable-next-line class-methods-use-this
  getUuidFromPath(location, routeType) {
    const pathname = location?.pathname || '';
    const matchedPath = pathname.match(new RegExp(`^/pages/[^/]+/${routeType}/([^/]+)$`, 'i'));
    return matchedPath?.[1] || '';
  }

  // eslint-disable-next-line class-methods-use-this
  isFileModalRoute(location) {
    const fileUuid = this.getUuidFromPath(location, 'file');

    return !!((
      (
        location.state
        && location.state.modal
        && this.previousLocation !== location
      )
      || uuidValidate(fileUuid)
    ));
  }

  isCollectionModalRoute(location) {
    const collectionUuid = this.getUuidFromPath(location, 'collection');
    return uuidValidate(collectionUuid);
  }

  isOverlayModalRoute(location) {
    return this.isFileModalRoute(location) || this.isCollectionModalRoute(location) ||
      /^\/pages\/[^/]+\/search(?:\/collections\/[^/]+)?\/?$/.test(location.pathname);
  }

  getPageId() {
    const {
      match,
      pageId: pageIdFromProps,
    } = this.props;
    const { pageId: pageIdFromUrl } = match.params;
    return pageIdFromUrl || pageIdFromProps;
  }

  fetchPage(accessRestrictionData = null) {
    const pageId = this.getPageId();
    this.pageMetadataController?.abort();
    this.pageMetadataController = new AbortController();
    const { signal } = this.pageMetadataController;
    const passwordSubmitted = !!accessRestrictionData?.password;
    return APIgetPage(pageId, {
      ...accessRestrictionData && { access_restriction_data: accessRestrictionData },
    }).then((response) => {
      if (signal.aborted) return;
      this.setState({
        page: response.data,
        passwordError: passwordSubmitted && !!response.data?.should_provide_password,
      }, () => this.checkAccess());
    }).catch((error) => {
      if (signal.aborted) return;
      this.setState({ passwordError: false });
      if (error.response?.status === 403 && error.response?.data?.error === 'login_required') {
        this.setState({ redirect: true });
        return;
      }

      if (error.response?.status === 403 && error.response?.data?.error === 'expired') {
        this.setState({ errorScreen: error.response?.data });
        return;
      }

      if (error.response?.status === 404) {
        this.setState({ notFound: true });
      }
    });
  }

  getPageContextValue(pageId, pageRouteId, visibilityMode = null) {
    if (
      !this.pageContextValue
      || this.pageContextValue.pageId !== pageId
      || this.pageContextValue.pageRouteId !== pageRouteId
      || this.pageContextValue.visibilityMode !== visibilityMode
    ) {
      this.pageContextValue = {
        pageId,
        pageRouteId,
        isEditing: false,
        pageBasePath: 'pages',
        visibilityMode,
      };
    }

    return this.pageContextValue;
  }

  getPuckRenderData({
    page,
    puckSections,
    disableTransparentHeaderPadding,
    pageAccentMode,
    pageAccentCustom,
    pagePrimaryFontValue,
    pageSecondaryFontValue,
  }) {
    const cache = this.puckRenderCache;
    const shouldReuse = (
      cache.page === page
      && cache.disableTransparentHeaderPadding === disableTransparentHeaderPadding
      && cache.pageAccentMode === pageAccentMode
      && cache.pageAccentCustom === pageAccentCustom
      && cache.pagePrimaryFontValue === pagePrimaryFontValue
      && cache.pageSecondaryFontValue === pageSecondaryFontValue
      && cache.data
    );

    if (shouldReuse) {
      return cache.data;
    }

    const data = {
      content: puckSections
        .map(section => ({
          type: section.type,
          props: { id: section.id, ...section },
        })),
      zones: page.options?.puck_zones || {},
      root: {
        props: {
          name: page.name || '',
          url: page.url || '',
          header: page.options?.header || 'solid',
          disableTransparentHeaderPadding,
          accentColor: {
            mode: pageAccentMode,
            color: pageAccentCustom || '#203444',
          },
          fontPrimary: pagePrimaryFontValue,
          fontSecondary: pageSecondaryFontValue,
          // eslint-disable-next-line no-underscore-dangle
          _bg: page.options?._bg || {
            type: 'none', color: '#ffffff', gradientStart: '#ffffff', gradientEnd: '#000000', gradientDeg: 135,
          },
        },
      },
    };

    this.puckRenderCache = {
      page,
      disableTransparentHeaderPadding,
      pageAccentMode,
      pageAccentCustom,
      pagePrimaryFontValue,
      pageSecondaryFontValue,
      data,
    };

    return data;
  }

  checkAccess() {
    const { dispatch } = this.props;
    const { page } = this.state;

    let locked = false;

    const providePassword = page?.should_provide_password;

    if (providePassword) {
      locked = true;
    }

    if (!isLoggedIn() && page && page.require_name) {
      const nameCookie = Cookies.get(`sp_content_portal_user_name_${page.id}`);

      if (!nameCookie) {
        locked = true;
      }
    }

    if (!isLoggedIn() && page && page.require_email) {
      const emailCookie = Cookies.get(`sp_content_portal_user_email_${page.id}`);

      if (!emailCookie) {
        locked = true;
      }
    }

    this.setState({
      accessLocked: locked,
    });
    if (!locked && page && !this.pageMetadataController.signal.aborted) {
      dispatch(fetchPageMetadata(page.id, {
        signal: this.pageMetadataController.signal,
      }));
    }
  }

  submitAccess(name = '', email = '', password = '') {
    return this.fetchPage({ name, email, password });
  }

  render() {
    const {
      profile,
      location,
      intl,
      selectedFiles,
      selectedCollections,
    } = this.props;
    const {
      page,
      error,
      redirect,
      notFound,
      accessLocked,
      errorScreen,
      passwordError,
    } = this.state;

    const isUserLoggedIn = isLoggedIn();
    const isFileModal = this.isFileModalRoute(location);
    const searchLocation = isFileModal ? this.previousLocation : location;
    const isSearchPage = /^\/pages\/[^/]+\/search(?:\/collections\/[^/]+)?\/?$/.test(
      searchLocation.pathname,
    );
    const isCollectionModal = this.isCollectionModalRoute(location);
    const shouldRenderCollectionUnderFileModal =
      isFileModal && this.isCollectionModalRoute(this.previousLocation);
    const shouldRenderCollectionModal =
      isCollectionModal || shouldRenderCollectionUnderFileModal;
    const collectionModalLocation = shouldRenderCollectionUnderFileModal
      ? this.previousLocation
      : location;

    const config = getPuckConfig(intl);

    const showSearchBar = () => (
      page.options?.showSearchBar ?? page.options?.show_search_bar ?? true
    );

    const getHeaderType = () => {
      const headerType = page.options.header || '';

      const showSearch = showSearchBar();

      if (showSearch) {
        return `${headerType}-search`;
      }
      return headerType;
    };

    if (errorScreen) {
      return (
        <div className={`layout layout-landing${getSetting('cs1') ? ' cs1' : ''}`}>
          <PageHeader limited={true} showSearchBar={false} />
          <p className="center mt-4">
            {errorScreen.message || 'An error occurred.'}
          </p>
        </div>
      );
    }

    if (accessLocked) {
      return (
        <>
          <PageHeader limited={true} showSearchBar={false} />
          <PageUnlockAccess
            page={page}
            providePassword={page.require_password}
            passwordError={passwordError}
            onSubmit={this.submitAccess}
            checkAccess={this.checkAccess}
          />
        </>
      );
    }

    if (redirect) {
      return (
        <Redirect
          to={{
            pathname: '/login',
            search: location.search,
            state: {
              from: `${location.pathname}${cleanLoginParams(location.search)}`,
            },
          }}
        />
      );
    }

    if (notFound) {
      return (
        <div className={`layout layout-landing${getSetting('cs1') ? ' cs1' : ''}`}>
          {isUserLoggedIn && <PageHeader />}
          <NothingFound />
        </div>
      );
    }

    if (error) {
      return (
        <div className={`layout layout-landing${getSetting('cs1') ? ' cs1' : ''}`}>
          <p className="center">
            {error}
          </p>
        </div>
      );
    }

    if (!profile) {
      return (
        <div className={`layout layout-landing${getSetting('cs1') ? ' cs1' : ''}`}>
          <LoadingMain />
        </div>
      );
    }

    if (!page) {
      return (
        <div className={`layout layout-landing${getSetting('cs1') ? ' cs1' : ''}`}>
          <div className="landing-content">
            <Loading />
          </div>
        </div>
      );
    }

    if (page.options.page_type === 'internal') {
      return (
        <Redirect
          to={{
            pathname: `${getPageUrl(page)}${isSearchPage ? '/search' : ''}`,
            search: location.search,
            state: {
              from: location.pathname,
            },
          }}
        />
      );
    }

    const isActionbarActive = selectedFiles.length > 0 || selectedCollections.length > 0;

    const pageRouteId = this.getPageId();
    const pageId = page?.id || pageRouteId;

    const {
      primaryValue: pagePrimaryFontValue,
      secondaryValue: pageSecondaryFontValue,
    } = resolvePageFontValues(page.options || {});
    const pageAccentMode = page?.options?.accent_color_mode === 'custom' ? 'custom' : 'workspace';
    const pageAccentCustom = page?.options?.accent_color_custom || '';
    const useCustomPageAccent = pageAccentMode === 'custom' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(pageAccentCustom);

    const { fontStyles, pageFontVars } = buildPageFontPresentation({
      primaryValue: pagePrimaryFontValue,
      secondaryValue: pageSecondaryFontValue,
      primaryUploadedUrl: page.options?.font_primary_url || page.options?.fontPrimary_url,
      secondaryUploadedUrl: page.options?.font_secondary_url || page.options?.fontSecondary_url,
    });

    const layoutClassName = classnames('layout', 'layout-landing', {
      cs1: getSetting('cs1'),
      'page-accent-custom': useCustomPageAccent,
      'layout-landing-with-actionbar': page.editor_type === 'puck' && isActionbarActive,
    });

    const layoutStyle = {
      ...pageFontVars,
      ...(useCustomPageAccent ? { '--page-accent-color': pageAccentCustom } : {}),
    };

    const puckSections = (page.sections || [])
      .filter(section => section.type && config.components[section.type]);
    const disableTransparentHeaderPadding = shouldDisableTransparentHeaderPadding(puckSections);
    const puckRenderData = this.getPuckRenderData({
      page,
      puckSections,
      disableTransparentHeaderPadding,
      pageAccentMode,
      pageAccentCustom,
      pagePrimaryFontValue,
      pageSecondaryFontValue,
    });

    const pageHeader = (
      <PageHeader
        headerType={getHeaderType()}
        pageLogoUrl={page.options.logo_url_preview}
        showSearchBar={page.options?.showSearchBar ?? page.options?.show_search_bar ?? true}
        pageNavigation={page.options?.pageNavigation || page.options?.page_navigation || []}
        pageBasePath="pages"
        pageRouteId={pageRouteId}
        pageId={pageId}
        pageVisibilityMode={page?.visibility_mode || page?.options?.visibility_mode || null}
      />
    );

    const content = (
      <div className={layoutClassName} style={layoutStyle}>
        {fontStyles.length > 0 && <style>{fontStyles.join('\n')}</style>}
        {pageHeader}

        {!isSearchPage && page.editor_type === 'puck' && (
          <>
            <Render
              config={config}
              data={puckRenderData}
            />
            <Actionbar />
          </>
        )}

        {!isSearchPage && page.editor_type !== 'puck' && (
          <div className="landing-content">
            {page.sections.map((section) => {
              const component = getSectionFrontendComponent(section.section);
              const SectionContent = sectionComponents[component];
              if (!SectionContent) {
                return null;
              }
              return (
                <SectionContent
                  key={section.id}
                  section={section}
                  page={page}
                  isLoggedIn={!!profile.uuid}
                />
              );
            })}
          </div>
        )}

        <PageFooter />
        <FlashMessages />
      </div>
    );

    return (
      <PageIdContext.Provider
        value={this.getPageContextValue(
          pageId,
          pageRouteId,
          page?.visibility_mode || page?.options?.visibility_mode || null,
        )}
      >
        {content}
        {isSearchPage && (
          <Route
            path="/pages/:pageId/search"
            location={searchLocation}
            render={() => (
              <ExternalSearchResults
                entityType="custom-page"
                entityId={pageId}
                pageHeader={pageHeader}
              />
            )}
          />
        )}
        {isFileModal ? (
          <Route
            path="/pages/:pageId/file/:fileId"
            render={() => (
              <File originalLocation={this.previousLocation} fixedModal={true} />
            )}
          />
        ) : null}
        {shouldRenderCollectionModal ? (
          <Route
            path="/pages/:pageId/collection/:collectionUuid"
            location={collectionModalLocation}
            render={() => (
              <PageCollectionView
                originalLocation={this.previousLocation}
                fixedModal={true}
              />
            )}
          />
        ) : null}
      </PageIdContext.Provider>
    );
  }
}

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  selectedFiles: state.Files.selectedFiles,
  selectedCollections: state.Collections.selectedCollections,
});

Page.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  profile: PropTypes.object,
  pageId: PropTypes.string,
  intl: PropTypes.object.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  selectedCollections: PropTypes.array.isRequired,
};

Page.defaultProps = {
  profile: null,
  pageId: null,
};

export default injectIntl(withRouter(connect(mapStateToProps)(Page)));
